import type { PersonReviewHistoryEntry, ReviewReport } from "@/hooks/use-review-report";

/**
 * A person's review scores, trended across cycles — DM-WF-001, EPIC-070
 * (WI-261).
 *
 * Pure derivation over the payload `usePersonReviewHistory` already returns.
 * That is the point: `reviewAudienceFor` and `getReviewReport` decided, per
 * review, how much of it this viewer may see, so there is nothing to redact
 * here and no second place for that decision to drift (WI-287). It also means
 * the SAME code serves HR and the subject — for a full reader peers arrive as
 * individual answers and for the subject as a pooled average, and both reduce
 * to one number per source per cycle.
 *
 * ## What is NOT detectable
 *
 * AC-742 names three things that break comparability: a changed criterion, a
 * changed scale, and a changed level. Two are visible in the data and are
 * handled below. A changed SCALE is not: the rungs come from the one global
 * `capability_level` ladder rather than a per-review snapshot, so editing it
 * silently re-reads every historical rating and leaves no trace to find. A
 * marker that can never fire would be worse than its absence — if that ever
 * needs closing, it wants a snapshot on the review, not a guess here.
 */

/**
 * The ordinal reviewers pick for "not applicable". Mirrors
 * `SCALE_NOT_APPLICABLE_ORDINAL` in `src/api/review-report.ts`, redeclared
 * rather than imported because a value import from `src/api/` pulls the server
 * logger and Drizzle into the browser bundle.
 */
export const NOT_APPLICABLE_ORDINAL = 6;

/** Self and manager first; everyone else after, in a stable order. */
const SOURCE_ORDER = ["self", "manager"];

export interface TrendCycle {
  reviewId: string;
  cycleName: string;
  periodEnd: string;
  roleLevelName: string | null;
}

export interface TrendSeries {
  relationshipKey: string;
  relationshipName: string;
  /**
   * True when the point is an anonymised average the subject is shown rather
   * than a named reviewer's own score. The chart says so, so nobody reads a
   * pooled line as one person's opinion.
   */
  isPooled: boolean;
  /** One entry per cycle, aligned with `cycles`. Null is "not rated". */
  points: (number | null)[];
}

export interface TrendSubject {
  key: string;
  name: string;
  series: TrendSeries[];
}

export interface TrendGroup extends TrendSubject {
  color: string | null;
  competencies: TrendSubject[];
}

export interface TrendBreak {
  /** The break sits between this cycle and the next. */
  afterIndex: number;
  reason: string;
}

export interface PersonReviewTrend {
  cycles: TrendCycle[];
  groups: TrendGroup[];
  breaks: TrendBreak[];
}

type Criterion = ReviewReport["criteria"][number];

/**
 * Where a competency with no category lands. Exported because no workspace has
 * categorised its competencies yet (checked 2026-10-05: zero `skill_category`
 * rows), so in practice this is the ONLY group — and a grouping tier holding
 * everything is a wrapper, not a grouping. The UI collapses to the competencies
 * themselves when there is just one group.
 */
export const UNGROUPED = "Other";

/** Mean of the ratings that actually carry one. Null when none do. */
function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * One number per rating source for a single competency in a single cycle.
 *
 * Individual answers are averaged within their relationship so that a full
 * reader's three named peers and the subject's one pooled peer average mean
 * the same thing on the chart.
 */
function sourcesFor(
  criterion: Criterion,
): Map<string, { name: string; value: number; pooled: boolean }> {
  const byKey = new Map<string, { name: string; value: number; pooled: boolean }>();

  const rated = new Map<string, { name: string; values: number[] }>();
  for (const answer of criterion.answers) {
    if (answer.ordinal === null || answer.ordinal >= NOT_APPLICABLE_ORDINAL) continue;
    const key = answer.relationshipKey ?? "rating";
    const bucket = rated.get(key) ?? { name: answer.relationshipName ?? "Rating", values: [] };
    bucket.values.push(answer.ordinal);
    rated.set(key, bucket);
  }
  for (const [key, bucket] of rated) {
    const value = mean(bucket.values);
    if (value !== null) byKey.set(key, { name: bucket.name, value, pooled: false });
  }

  for (const group of criterion.groups) {
    if (group.averageOrdinal === null) continue;
    // A pool never collides with a named answer: the server puts a reviewer in
    // one or the other, never both.
    byKey.set(group.relationshipKey, {
      name: group.relationshipName,
      value: group.averageOrdinal,
      pooled: true,
    });
  }

  return byKey;
}

/** Self, then manager, then everyone else alphabetically. */
function compareSources(a: TrendSeries, b: TrendSeries): number {
  const rank = (key: string) => {
    const index = SOURCE_ORDER.indexOf(key);
    return index === -1 ? SOURCE_ORDER.length : index;
  };
  const byRank = rank(a.relationshipKey) - rank(b.relationshipKey);
  return byRank !== 0 ? byRank : a.relationshipName.localeCompare(b.relationshipName);
}

/**
 * Turns per-cycle readings into plotted series, dropping any source that never
 * gets two points to join — a single dot is not a trend, and an axis drawn for
 * one is noise.
 */
function seriesFrom(
  readings: Map<string, { name: string; pooled: boolean; points: (number | null)[] }>,
): TrendSeries[] {
  const series: TrendSeries[] = [];
  for (const [relationshipKey, reading] of readings) {
    if (reading.points.filter((p) => p !== null).length < 2) continue;
    series.push({
      relationshipKey,
      relationshipName: reading.name,
      isPooled: reading.pooled,
      points: reading.points,
    });
  }
  return series.sort(compareSources);
}

function emptyPoints(length: number): (number | null)[] {
  return Array.from({ length }, () => null);
}

export function buildPersonReviewTrend(entries: PersonReviewHistoryEntry[]): PersonReviewTrend {
  // The history endpoint sorts newest-first; a chart reads left to right.
  const ordered = [...entries].sort((a, b) => a.periodEnd.localeCompare(b.periodEnd));
  const cycles: TrendCycle[] = ordered.map((entry) => ({
    reviewId: entry.reviewId,
    cycleName: entry.cycleName,
    periodEnd: entry.periodEnd,
    roleLevelName: entry.report.roleLevelName,
  }));

  // One cycle cannot trend against anything.
  if (ordered.length < 2) return { cycles, groups: [], breaks: [] };

  const breaks: TrendBreak[] = [];
  for (let i = 1; i < cycles.length; i++) {
    const before = cycles[i - 1]?.roleLevelName;
    const after = cycles[i]?.roleLevelName;
    // An unrecorded level is not evidence of a change.
    if (!before || !after || before === after) continue;
    breaks.push({
      afterIndex: i - 1,
      reason: `Level changed from ${before} to ${after} — ratings either side aren't comparable`,
    });
  }

  interface Reading {
    name: string;
    pooled: boolean;
    points: (number | null)[];
  }
  interface CompetencyAccumulator {
    name: string;
    groupName: string;
    groupColor: string | null;
    readings: Map<string, Reading>;
  }

  const competencies = new Map<string, CompetencyAccumulator>();

  ordered.forEach((entry, cycleIndex) => {
    for (const criterion of entry.report.criteria) {
      // The competency is the identity that survives across rounds; the
      // question is one row on one review. Falling back to the question id
      // keeps custom questions apart from each other rather than merging every
      // one of them under a single null key.
      const key = criterion.competencyId ?? `question:${criterion.questionId}`;
      // The competency's own name where there is one; a question written for
      // the occasion has only its wording to go by.
      const label = criterion.competencyName ?? criterion.name;
      const accumulator = competencies.get(key) ?? {
        name: label,
        groupName: criterion.categoryName ?? UNGROUPED,
        groupColor: criterion.categoryColor,
        readings: new Map<string, Reading>(),
      };
      // Cycles are walked oldest-first, so the LAST one wins: a question that
      // has been reworded or regrouped should read as whatever it is called
      // now, not as whatever it was called years ago.
      accumulator.name = label;
      accumulator.groupName = criterion.categoryName ?? UNGROUPED;
      accumulator.groupColor = criterion.categoryColor;

      for (const [relationshipKey, source] of sourcesFor(criterion)) {
        const reading = accumulator.readings.get(relationshipKey) ?? {
          name: source.name,
          pooled: source.pooled,
          points: emptyPoints(ordered.length),
        };
        reading.points[cycleIndex] = source.value;
        // A reader who gains access mid-history could see a source pooled in
        // one cycle and named in another; the cautious label wins.
        reading.pooled = reading.pooled || source.pooled;
        accumulator.readings.set(relationshipKey, reading);
      }

      competencies.set(key, accumulator);
    }
  });

  const grouped = new Map<
    string,
    { color: string | null; entries: [string, CompetencyAccumulator][] }
  >();
  for (const entry of competencies) {
    const [, accumulator] = entry;
    const group = grouped.get(accumulator.groupName) ?? {
      color: accumulator.groupColor,
      entries: [],
    };
    group.entries.push(entry);
    grouped.set(accumulator.groupName, group);
  }

  const groups: TrendGroup[] = [];
  for (const [groupName, group] of grouped) {
    const children: TrendSubject[] = [];
    // Readings for the group's own line: each cycle's mean across whichever of
    // its competencies were rated that cycle.
    const rollup = new Map<string, { name: string; pooled: boolean; values: number[][] }>();

    for (const [key, accumulator] of group.entries) {
      const series = seriesFrom(accumulator.readings);
      if (series.length > 0) children.push({ key, name: accumulator.name, series });

      for (const [relationshipKey, reading] of accumulator.readings) {
        const target = rollup.get(relationshipKey) ?? {
          name: reading.name,
          pooled: reading.pooled,
          values: Array.from({ length: ordered.length }, (): number[] => []),
        };
        reading.points.forEach((point, index) => {
          if (point !== null) target.values[index]?.push(point);
        });
        target.pooled = target.pooled || reading.pooled;
        rollup.set(relationshipKey, target);
      }
    }

    if (children.length === 0) continue;

    const groupReadings = new Map<string, Reading>();
    for (const [relationshipKey, target] of rollup) {
      groupReadings.set(relationshipKey, {
        name: target.name,
        pooled: target.pooled,
        points: target.values.map((values) => mean(values)),
      });
    }

    groups.push({
      key: groupName,
      name: groupName,
      color: group.color,
      series: seriesFrom(groupReadings),
      competencies: children.sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  return {
    cycles,
    groups: groups.sort((a, b) => a.name.localeCompare(b.name)),
    breaks,
  };
}
